import React, { useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getSupabaseClient } from "../services/supabaseClient";
import { evaluatePasswordStrength } from "../utils/passwordPolicy";
import MFAManagement from "./MFAManagement";
import SessionManagement from "./SessionManagement";
import "../styles/profile.css";
import "../styles/password.css";

const formatDateTime = (value) => {
    if (!value) {
        return "Unknown";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return "Unknown";
    }
    return parsed.toLocaleString();
};

export default function ProfilePage() {
    const { user, role } = useAuth();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const passwordEvaluation = useMemo(() => {
        return evaluatePasswordStrength(newPassword, { role });
    }, [newPassword, role]);

    const strengthPercent = useMemo(() => {
        return Math.max(20, (passwordEvaluation.score + 1) * 20);
    }, [passwordEvaluation]);

    const canSubmit =
        currentPassword &&
        newPassword &&
        confirmPassword &&
        newPassword === confirmPassword &&
        passwordEvaluation.isValid;

    const handleChangePassword = async (event) => {
        event.preventDefault();
        if (!user) {
            return;
        }
        setError(null);
        setStatus(null);

        if (newPassword !== confirmPassword) {
            setError("New passwords do not match.");
            return;
        }

        if (!passwordEvaluation.isValid) {
            setError("Please meet all password requirements.");
            return;
        }

        setSaving(true);
        try {
            const supabase = getSupabaseClient();
            if (typeof supabase.auth.reauthenticate === "function") {
                const { error: reauthError } = await supabase.auth.reauthenticate({
                    password: currentPassword,
                });
                if (reauthError) {
                    throw reauthError;
                }
            } else {
                const { error: reauthError } = await supabase.auth.signInWithPassword({
                    email: user.email,
                    password: currentPassword,
                });
                if (reauthError) {
                    throw reauthError;
                }
            }

            const { error: updateError } = await supabase.auth.updateUser({
                password: newPassword,
            });

            if (updateError) {
                throw updateError;
            }

            setStatus("Password updated successfully.");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err) {
            setError(err?.message || "Unable to update password.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <main className="profile-page">
            <div className="profile-shell">
                <div className="profile-card">
                    <div className="profile-card-header">
                        <div>
                            <h1>Profile & Settings</h1>
                            <p className="text-muted">
                                Manage your account security settings.
                            </p>
                        </div>
                        <span className={`status-pill ${user?.email_confirmed_at ? "enabled" : "disabled"}`}>
                            {user?.email_confirmed_at ? "Email verified" : "Email unverified"}
                        </span>
                    </div>

                    <div className="profile-grid">
                        <div>
                            <span className="field-label">Email</span>
                            <p>{user?.email || "Unknown"}</p>
                        </div>
                        <div>
                            <span className="field-label">Full name</span>
                            <p>{user?.user_metadata?.full_name || "Not set"}</p>
                        </div>
                        <div>
                            <span className="field-label">Role</span>
                            <p>{role === "internal_staff" ? "Internal staff" : "Client"}</p>
                        </div>
                        <div>
                            <span className="field-label">Account created</span>
                            <p>{formatDateTime(user?.created_at)}</p>
                        </div>
                    </div>
                </div>

                <div className="profile-card">
                    <h2>Change password</h2>
                    <form className="profile-form" onSubmit={handleChangePassword}>
                        <label className="form-field">
                            <span>Current password</span>
                            <input
                                type="password"
                                autoComplete="current-password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                required
                            />
                        </label>
                        <label className="form-field">
                            <span>New password</span>
                            <input
                                type="password"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                            />
                        </label>
                        <label className="form-field">
                            <span>Confirm new password</span>
                            <input
                                type="password"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                        </label>

                        <div className="password-strength">
                            <div className="password-strength-meta">
                                <span className="strength-label">
                                    Strength: {passwordEvaluation.scoreLabel}
                                </span>
                                <span className="strength-score">
                                    Minimum score: {passwordEvaluation.requiredScore}
                                </span>
                            </div>
                            <div className="password-strength-bar">
                                <div
                                    className={`password-strength-fill ${passwordEvaluation.scoreClass}`}
                                    style={{ width: `${strengthPercent}%` }}
                                />
                            </div>
                            <div className="password-requirements">
                                <div
                                    className={`requirement ${
                                        passwordEvaluation.requirements.length ? "met" : ""
                                    }`}
                                >
                                    At least {passwordEvaluation.minLength} characters
                                </div>
                                <div
                                    className={`requirement ${
                                        passwordEvaluation.requirements.uppercase ? "met" : ""
                                    }`}
                                >
                                    One uppercase letter
                                </div>
                                <div
                                    className={`requirement ${
                                        passwordEvaluation.requirements.lowercase ? "met" : ""
                                    }`}
                                >
                                    One lowercase letter
                                </div>
                                <div
                                    className={`requirement ${
                                        passwordEvaluation.requirements.number ? "met" : ""
                                    }`}
                                >
                                    One number
                                </div>
                                <div
                                    className={`requirement ${
                                        passwordEvaluation.requirements.special ? "met" : ""
                                    }`}
                                >
                                    One special character (!@#$%^&*)
                                </div>
                                <div
                                    className={`requirement ${
                                        passwordEvaluation.requirements.score ? "met" : ""
                                    }`}
                                >
                                    Strength score {passwordEvaluation.requiredScore}+
                                </div>
                            </div>
                        </div>

                        {error && <p className="error-message">{error}</p>}
                        {status && <p className="success-message">{status}</p>}

                        <button
                            className="btn primary"
                            type="submit"
                            disabled={!canSubmit || saving}
                        >
                            {saving ? "Updating..." : "Update password"}
                        </button>
                    </form>
                </div>

                <MFAManagement />
                <SessionManagement />
            </div>
        </main>
    );
}
